import {useNavigation, useRoute} from '@react-navigation/native';
import React, {useCallback, useEffect, useState} from 'react';
import {
  ActivityIndicator,
  Alert,
  ScrollView,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';

import Header from '../../components/Header/Header';
import SearchHeader from '../../components/SearchHeader/SearchHeader';
import {Colors} from '../../constants/Colors';
import {NavigationProps} from '../../navigation/Routes';
import {AppRoutes} from '../../navigation/Routes.types';
import ScheduledProposalService, {
  ScheduledOrderPreviewLine,
  ScheduledSetBonus,
  ScheduledSetPreview,
  ScheduledSetSale,
} from '../../services/ScheduledProposalService/ScheduledProposalService';
import {useUserStore} from '../../store/UserStore';
import {montarSacolaDaProposta} from '../../utils/seguirParaCheckout';
import {styles} from './styles';

const BRL = (valor: number): string =>
  (valor ?? 0).toLocaleString('pt-BR', {style: 'currency', currency: 'BRL'});

/**
 * O nome do produto é `descricao` + `complemento` — nunca só a descrição.
 *
 * É o complemento que carrega embalagem e tamanho: sem ele, "Coca-Cola Sem
 * Açúcar" é o nome de todos os formatos do mesmo SKU, e o cliente compara o
 * carrinho programado com a sacola sem reconhecer o item. Mesma composição de
 * `BagList`, do catálogo e da tela de produto, com a mesma limpeza — o
 * complemento vem do SAP com o preço colado (`- R$`) e com `\n` literal.
 */
const nomeProduto = (
  descricao?: string | null,
  complemento?: string | null,
): string =>
  `${descricao ?? ''} ${(complemento ?? '')
    .replace('- R$', '')
    .replace(/\\n/g, '')}`
    .replace(/\s+/g, ' ')
    .trim();

/** `2026-08-14` -> `14/08/2026`. Sem `new Date`: o parse de data pura vira UTC
 * e volta um dia no fuso do Brasil. */
const dataBR = (iso?: string | null): string => {
  if (!iso) {
    return '';
  }
  const [ano, mes, dia] = iso.split('-');
  return dia && mes && ano ? `${dia}/${mes}/${ano}` : iso;
};

/*
 * Componentes de apresentação em escopo de MÓDULO, e não dentro da tela.
 * Definidos lá dentro, o React vê um tipo novo a cada render e destrói a
 * subárvore inteira — num carrinho com n vendas isso é a lista toda
 * remontando a cada toque.
 */

const Linha = ({linha}: {linha: ScheduledOrderPreviewLine}) => {
  const removido = linha.acao === 'REMOVIDO';
  const reduzido = linha.acao === 'REDUZIDO';

  return (
    <View style={[styles.linha, removido && styles.linhaRemovida]}>
      <Text
        style={[styles.linhaTitulo, removido && styles.textoRiscado]}
        numberOfLines={2}>
        {nomeProduto(linha.descricao, linha.produto?.complemento) || linha.sku}
      </Text>

      {removido ? (
        <Text style={styles.linhaRemovidaTexto}>
          Sem estoque — item removido do pedido
        </Text>
      ) : (
        <>
          <View style={styles.linhaValores}>
            <Text style={styles.linhaTexto}>
              {reduzido ? (
                <>
                  <Text style={styles.textoRiscado}>
                    {linha.quantidadeProgramada}
                  </Text>
                  {'  '}
                  <Text style={styles.linhaDestaque}>
                    {linha.quantidadeAjustada}
                  </Text>
                </>
              ) : (
                `${linha.quantidadeAjustada}`
              )}
              {' un. × '}
              {BRL(linha.precoAjustado)}
            </Text>
            <Text style={styles.linhaSubtotal}>
              {BRL(linha.subtotalAjustado)}
            </Text>
          </View>

          {reduzido ? (
            <Text style={styles.linhaAviso}>
              Estoque insuficiente: quantidade reduzida de{' '}
              {linha.quantidadeProgramada} para {linha.quantidadeAjustada}.
            </Text>
          ) : null}

          {/* O caso que surpreende: menos produto, preço unitário maior.
              Sem esta linha o cliente só descobriria pelo total. */}
          {linha.precoSubiu ? (
            <Text style={styles.linhaAvisoPreco}>
              O preço unitário subiu de {BRL(linha.precoProgramado)} para{' '}
              {BRL(linha.precoAjustado)} porque a quantidade saiu da faixa de
              desconto.
            </Text>
          ) : null}
        </>
      )}
    </View>
  );
};

/** Um bloco por venda — elas NÃO são fundidas. */
const BlocoVenda = ({
  venda,
  indice,
  total,
}: {
  venda: ScheduledSetSale;
  indice: number;
  total: number;
}) => (
  <View
    style={[
      styles.blocoVenda,
      (venda.rupturaTotal || venda.jaPedida) && styles.blocoVendaInativo,
    ]}>
    <View style={styles.blocoVendaCabecalho}>
      <Text style={styles.blocoVendaTitulo}>
        {total > 1 ? `Pedido ${indice + 1} de ${total}` : 'Seu pedido'}
      </Text>
      {venda.jaPedida ? (
        <Text style={styles.blocoVendaSelo}>Já enviado</Text>
      ) : venda.rupturaTotal ? (
        <Text style={styles.blocoVendaSeloAlerta}>Sem estoque</Text>
      ) : null}
    </View>

    {venda.rupturaTotal ? (
      // Ruptura TOTAL desta venda: ela não gera pedido, e as demais do
      // conjunto seguem aceitáveis. Decisão do sistema, não do cliente.
      <Text style={styles.blocoVendaTextoAlerta}>
        Nenhum item deste pedido está disponível em estoque. Ele não será
        enviado, e os demais pedidos seguem normalmente.
      </Text>
    ) : (
      <>
        {venda.linhas.map((linha, index) => (
          <Linha key={`${linha.sku ?? 'item'}-${index}`} linha={linha} />
        ))}

        {venda.indisponiveis?.length ? (
          <View style={styles.blocoIndisponiveis}>
            <Text style={styles.blocoIndisponiveisTitulo}>
              Itens não disponíveis para a sua loja
            </Text>
            {venda.indisponiveis.map((item, index) => (
              <Text
                key={`${item.sku}-${index}`}
                style={styles.blocoIndisponiveisTexto}>
                {nomeProduto(item.description, item.complemento) || item.sku} (
                {item.quantity} un.)
              </Text>
            ))}
          </View>
        ) : null}

        <View style={styles.blocoVendaTotal}>
          <Text style={styles.blocoVendaTotalRotulo}>Subtotal</Text>
          <Text style={styles.blocoVendaTotalValor}>
            {BRL(venda.totalAjustado)}
          </Text>
        </View>
      </>
    )}
  </View>
);

/** As bonificações: itens sem cobrança, já reservados no SAP. */
const BlocoBonificacoes = ({itens}: {itens: ScheduledSetBonus[]}) => (
  <View style={styles.blocoBonificacao}>
    <Text style={styles.blocoBonificacaoTitulo}>
      Bonificação {itens.length > 1 ? `(${itens.length})` : ''}
    </Text>
    {/* Explicar o que elas são evita a pergunta óbvia: por que não somam no
        total, e por que não dá para recusá-las. */}
    <Text style={styles.blocoBonificacaoTexto}>
      Produtos sem cobrança, já reservados pelo seu vendedor. Eles não entram
      no valor a pagar.
    </Text>

    {itens.map(bonificacao =>
      bonificacao.linhas.map((linha, index) => (
        <View
          key={`${bonificacao.orderApplicationId}-${linha.sku ?? index}`}
          style={styles.blocoBonificacaoLinha}>
          <Text style={styles.blocoBonificacaoLinhaTexto} numberOfLines={2}>
            {nomeProduto(linha.descricao, linha.complemento) || linha.sku}
          </Text>
          <Text style={styles.blocoBonificacaoLinhaQtd}>
            {linha.quantidade} un.
          </Text>
        </View>
      )),
    )}
  </View>
);


/**
 * PED-13 RN-20/RN-21/RN-47 — o carrinho SOMENTE LEITURA do CONJUNTO.
 *
 * ## O carrinho é do conjunto, não de uma proposta
 *
 * Refino de 10/08/2026: a unidade é o CONJUNTO (cliente + data + figura), com
 * *n* vendas e *m* bonificações. Esta tela é a projeção dele — UMA visão com
 * tudo. *"O cliente não navega entre n carrinhos nem confirma um por um."*
 *
 * ## O que esta tela substitui
 *
 * Antes o pedido programado era despejado na SACOLA NORMAL, editável, com o
 * cliente livre para mexer. Isso está fechado: sem alteração de quantidade, sem
 * inclusão, sem remoção — e sem remover uma venda do conjunto. Por isso aqui não
 * existe `+`, `−`, lixeira, campo de quantidade nem seletor por venda. Quem
 * edita é o sistema, e só por estoque.
 *
 * ## As vendas não são fundidas
 *
 * Fusão de carrinhos foi descartada: exigiria motor de mesclagem e decidir qual
 * tabela de preço prevalece. O aceite gera UMA ORDEM POR VENDA, e a tela mostra
 * um bloco por venda para refletir a estrutura que o pedido vai ter.
 *
 * ## As bonificações estão aqui, mas não estão sendo aceitas
 *
 * Elas já existem como ordem no SAP desde a programação. Aparecem porque é o
 * pacote comercial inteiro que o cliente negociou — *a Brasal não dá desconto em
 * nota, dá produto* — e por isso vêm sem preço e fora do total.
 *
 * ## Reconstruída a cada entrada
 *
 * O GET é sem estado e relê o conjunto inteiro. Quem abandona e volta pode ver
 * um carrinho diferente: por acréscimo do vendedor, por cancelamento de
 * bonificação ou por estoque. É também o que atende RN-25 sem trava nenhuma — o
 * cliente confirma exatamente o que está na tela dele, e nada o interrompe no
 * meio do checkout.
 *
 * ## O antes e o depois não é enfeite
 *
 * A tabela de venda é uma escala por quantidade. Quando a ruptura reduz a
 * quantidade e cruza um degrau para baixo, o preço unitário **sobe** — e o total
 * pode ficar MAIOR com MENOS produto. Risco aceito, tratado por transparência.
 */
const ScheduledOrderReview = () => {
  const {navigate, goBack} = useNavigation<NavigationProps>();
  const route = useRoute<any>();
  const {user} = useUserStore();

  const setId: string = route?.params?.setId ?? '';
  // O código do conjunto vence o da sessão: no agrupamento, o conjunto aberto
  // pelo menu pode ser de outra loja do mesmo dono.
  const clientCode: string =
    route?.params?.clientCode ||
    user?.current_client?.clientCode ||
    user?.clientCode ||
    '';

  const [preview, setPreview] = useState<ScheduledSetPreview | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [erro, setErro] = useState<boolean>(false);

  const carregar = useCallback(async () => {
    if (!setId || !clientCode) {
      setLoading(false);
      setErro(true);
      return;
    }
    setLoading(true);
    setErro(false);
    try {
      setPreview(
        await ScheduledProposalService.GetScheduledSetPreview(
          clientCode,
          setId,
        ),
      );
    } catch (error) {
      console.error('Erro ao carregar o pedido programado:', error);
      setErro(true);
    } finally {
      setLoading(false);
    }
  }, [setId, clientCode]);

  useEffect(() => {
    carregar();
  }, [carregar]);

  /** As vendas que ainda vão virar pedido: nem em ruptura, nem já pagas. */
  const pendentes = (preview?.vendas ?? []).filter(
    venda => !venda.rupturaTotal && !venda.jaPedida,
  );

  /**
   * Aceitar é dizer sim ao CONJUNTO INTEIRO — não existe escolher qual venda.
   *
   * Um toque aceita o conjunto inteiro. O PAGAMENTO é por venda: cada uma vira
   * um pedido próprio, pelo checkout de sempre — boleto, PIX ou cartão. O
   * cliente paga uma, volta pelo menu e a próxima o espera.
   *
   * Agregar os n pagamentos numa transação só continua fora de escopo: PAG-01
   * entregou o pedido À VISTA (tela própria, `AVistaOrderReview`), que é outra
   * coisa — um pedido do dia, pago na hora, sem aceite. A agregação de vários
   * pagamentos programados num só não foi tratada por ele.
   */
  const [confirmando, setConfirmando] = useState<boolean>(false);

  /**
   * Monta a sacola com a venda escolhida e segue para o checkout de sempre.
   *
   * O miolo mora em `montarSacolaDaProposta` porque a tela do pedido à vista
   * precisa dele palavra por palavra — ver o docblock de lá. Duplicar as ~40
   * linhas garantiria divergência na primeira correção feita só de um lado, e o
   * sintoma seria um pedido finalizado diferente do carrinho aprovado.
   */
  const seguirParaCheckout = (proxima: ScheduledSetSale) => {
    if (!preview) {
      return;
    }

    const montou = montarSacolaDaProposta(
      proxima.linhas,
      proxima.orderApplicationId,
      preview.referenceDate,
      // Programada: o checkout segue oferecendo os três meios de pagamento.
      'A_PRAZO',
    );

    if (!montou) {
      return;
    }

    // Daqui em diante é o CHECKOUT DE SEMPRE: o cliente escolhe boleto, PIX ou
    // cartão, e o pedido nasce pelo caminho que já existe para cada um — em
    // boleto na confirmação do pedido, em PIX e cartão na confirmação do
    // PAGAMENTO (RN-19 regra 5). O conjunto fecha quando a última venda virar
    // pedido; até lá o cliente volta pelo menu e retoma de onde parou.
    navigate(AppRoutes.CHECKOUT);
  };

  const confirmar = async () => {
    if (!preview || !pendentes.length) {
      // O botão já está escondido nesses casos; a guarda é a segunda porta,
      // para um toque que chegue antes do próximo render.
      Alert.alert(
        'Pedido indisponível',
        'Nenhum item deste pedido está disponível no momento. Fale com seu vendedor.',
      );
      return;
    }

    // Reentrada: o conjunto já foi aceito antes (há venda `jaPedida`) e só
    // falta pagar o que sobrou. O aceite é o ATO ÚNICO que reparte o estoque e
    // cria as N ordens (RN-20/RN-21) — chamá-lo de novo repetiria esse ato
    // para um conjunto já aceito, e é aí que o servidor pode devolver de novo
    // uma venda já enviada como "a pagar". A próxima venda pendente já está no
    // preview, sem precisar de outro aceite.
    if (preview.vendas.some(venda => venda.jaPedida)) {
      seguirParaCheckout(pendentes[0]);
      return;
    }

    setConfirmando(true);

    // RN-20 — o ATO DO ACEITE cria as N ORDENS DE VENDA no servidor, num laço:
    // conjunto com três vendas gera três pedidos, sem fusão de carrinhos. O app
    // não monta sacola nem passa pelo checkout aqui — quem cria é o CB, com as
    // quantidades já repartidas pelo FIFO e com a data de referência e a figura
    // congeladas no conjunto.
    //
    // Em boleto o cliente paga n vezes, e isso é o comportamento normal do
    // meio: os n boletos nascem juntos. Agregar os n pagamentos numa transação
    // só continua fora de escopo — PAG-01 entregou o pedido À VISTA, que é
    // outra coisa.
    let aceite;
    try {
      aceite = await ScheduledProposalService.AcceptScheduledSet(
        clientCode,
        preview.setId,
      );
    } catch (error: any) {
      setConfirmando(false);

      if (error?.response?.data?.error === 'DEBT_OVERDUE') {
        Alert.alert(
          'Regularize seu débito',
          'Você tem título vencido há mais de ' +
            `${error.response.data.diasDeTolerancia} dias. ` +
            'Regularize para confirmar este pedido.',
        );
        carregar();
        return;
      }

      if (error?.response?.data?.error === 'SET_OUT_OF_STOCK') {
        Alert.alert(
          'Pedido indisponível',
          'Nenhum item deste pedido está disponível no momento. Fale com seu vendedor.',
        );
        carregar();
        return;
      }

      Alert.alert(
        'Não conseguimos confirmar seu pedido',
        'Tente novamente em alguns instantes.',
      );
      return;
    }

    setConfirmando(false);

    if (!aceite?.aPagar?.length) {
      Alert.alert(
        'Pedido indisponível',
        'Nenhum item deste pedido está disponível no momento. Fale com seu vendedor.',
      );
      carregar();
      return;
    }

    // A PRIMEIRA da fila do SERVIDOR, não da tela: entre a leitura e o toque o
    // estoque pode ter mudado, e é a repartição do ato do aceite que vale. Mas
    // nunca uma venda já enviada — se o servidor apontar para uma (resposta
    // obsoleta de uma reentrada), a defesa é a próxima pendente da tela: quem
    // decide o que ainda falta pagar é `jaPedida`, não a posição na resposta.
    const primeiroId = aceite.aPagar[0].orderApplicationId;
    const candidata = preview.vendas.find(
      v => v.orderApplicationId === primeiroId,
    );
    const proxima =
      candidata && !candidata.jaPedida ? candidata : pendentes[0];

    seguirParaCheckout(proxima);
  };

  if (loading) {
    return (
      <View style={styles.container}>
        <Header justDropDown={false} />
        <ActivityIndicator
          style={styles.spinner}
          color={Colors.RedPallete.red}
        />
      </View>
    );
  }

  if (erro || !preview) {
    return (
      <View style={styles.container}>
        <Header justDropDown={false} />
        <View style={styles.estadoVazio}>
          <Text style={styles.estadoVazioTitulo}>
            Não conseguimos carregar este pedido programado
          </Text>
          <TouchableOpacity onPress={carregar}>
            <Text style={styles.tentarNovamente}>Tentar novamente</Text>
          </TouchableOpacity>
          <TouchableOpacity onPress={goBack}>
            <Text style={styles.voltar}>Voltar</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  const totalDeVendas = preview.vendas.length;

  /* O valor do que já virou pedido. Vem do servidor; a soma dos blocos em tela
     é a rede de segurança para o app que roda contra um backend anterior a
     `totalEnviado` — sem ela o cliente veria o zero de novo. */
  const temPendentes = pendentes.length > 0;
  const temEnviadas = preview.vendas.some(venda => venda.jaPedida);
  const totalEnviado =
    preview.totalEnviado ??
    preview.vendas
      .filter(venda => venda.jaPedida)
      .reduce((soma, venda) => soma + (venda.totalAjustado ?? 0), 0);
  /* Nada mais a confirmar, e há pedido enviado: o total muda de pergunta —
     deixa de ser "quanto vou pagar se aceitar" e passa a ser "quanto foi". */
  const mostrarEnviado = !temPendentes && temEnviadas;

  return (
    <View style={styles.container}>
      <Header justDropDown={false} />

      <ScrollView contentContainerStyle={styles.conteudo}>
        {/* A saída do rodapé ("Agora não") não basta: o carrinho de um
            conjunto com várias vendas rola muito, e o cliente precisa poder
            voltar sem chegar ao fim da lista. */}
        <SearchHeader onPress={goBack} title="Pedido programado" mb={4} />
        <Text style={styles.subtituloTela}>
          Programado para {dataBR(preview.referenceDate)}
        </Text>

        {/* RN-24 — a DATA REAL DE ENTREGA, em todo aceite, inclusive quando
            coincide: o cliente confirma sabendo em que dia recebe. Quando
            diverge, aparece destacada — e não bloqueia nada, porque informar é
            o tratamento.

            É esta linha que dispensa qualquer vigilância de mudança de rota:
            rota alterada, feriado na data, calendário mudado e cliente que
            trocou de rota caem todos aqui. */}
        {preview.dataRealEntrega ? (
          <View
            style={[
              styles.blocoEntrega,
              preview.divergeDaProgramada && styles.blocoEntregaDivergente,
            ]}>
            <Text
              style={[
                styles.blocoEntregaTexto,
                preview.divergeDaProgramada &&
                  styles.blocoEntregaTextoDivergente,
              ]}>
              {preview.divergeDaProgramada
                ? `A entrega será em ${dataBR(
                    preview.dataRealEntrega,
                  )}, e não na data programada.`
                : `Entrega prevista para ${dataBR(preview.dataRealEntrega)}.`}
            </Text>
          </View>
        ) : null}

        {/* O cliente precisa saber que não é uma sacola comum ANTES de procurar
            o botão de editar que não existe. */}
        <View style={styles.avisoSomenteLeitura}>
          <Text style={styles.avisoSomenteLeituraTexto}>
            Este pedido foi montado pelo seu vendedor e não pode ser editado.
            Você pode aceitar ou não aceitar.
          </Text>
        </View>

        {preview.houveAjuste ? (
          <View style={styles.avisoAjuste}>
            <Text style={styles.avisoAjusteTitulo}>
              Ajustamos seu pedido por disponibilidade de estoque
            </Text>
            <Text style={styles.avisoAjusteTexto}>
              Confira as quantidades{preview.precoMudou ? ' e os preços' : ''}{' '}
              antes de confirmar.
            </Text>
          </View>
        ) : null}

        {preview.vendas.map((venda, index) => (
          <BlocoVenda
            key={venda.orderApplicationId}
            venda={venda}
            indice={index}
            total={totalDeVendas}
          />
        ))}

        {preview.bonificacoes.length ? (
          <BlocoBonificacoes itens={preview.bonificacoes} />
        ) : null}

        <View style={styles.totais}>
          {/* O "antes" só aparece quando existe diferença: repetir o mesmo
              número duas vezes ensina o cliente a ignorar o bloco. E ele fala
              do que ainda há a confirmar — sem pendente, não tem o que
              comparar. */}
          {temPendentes && (preview.houveAjuste || preview.precoMudou) ? (
            <View style={styles.totalLinha}>
              <Text style={styles.totalRotulo}>Total programado</Text>
              <Text style={[styles.totalValor, styles.textoRiscado]}>
                {BRL(preview.totalProgramado)}
              </Text>
            </View>
          ) : null}

          {/* Aceite parcial (uma venda falhou e está sendo reprocessada): o que
              já saiu aparece à parte, para não se somar ao que ainda depende de
              um toque nem sumir da conta. */}
          {temPendentes && temEnviadas ? (
            <View style={styles.totalLinha}>
              <Text style={styles.totalRotulo}>Pedidos já enviados</Text>
              <Text style={styles.totalValor}>{BRL(totalEnviado)}</Text>
            </View>
          ) : null}

          {/* Depois do aceite não há mais nada a pagar NESTA TELA — mas há o
              valor dos pedidos que nasceram dela, e é ele que o cliente procura
              aqui. "Total a pagar R$ 0,00" logo depois de confirmar milhares de
              reais se lê como pedido que não foi registrado. */}
          <View style={styles.totalLinha}>
            <Text style={styles.totalRotuloForte}>
              {mostrarEnviado ? 'Total dos pedidos enviados' : 'Total a pagar'}
            </Text>
            <Text style={styles.totalValorForte}>
              {BRL(mostrarEnviado ? totalEnviado : preview.totalAjustado)}
            </Text>
          </View>
          {/* Bonificação não entra no total, e dizer isso aqui é onde o cliente
              procura a conta. */}
          {preview.bonificacoes.length ? (
            <Text style={styles.totalObservacao}>
              A bonificação não é cobrada e não está somada acima.
            </Text>
          ) : null}
        </View>

        {preview.bloqueioFinanceiro ? (
          // RN-23 item 3 — não existe aceite parcial por motivo financeiro. A
          // saída em tela é orientar a regularização e dizer até quando a
          // janela está aberta.
          <View style={styles.blocoInviavel}>
            <Text style={styles.blocoInviavelTitulo}>
              Regularize seu débito para confirmar
            </Text>
            <Text style={styles.blocoInviavelTexto}>
              Você tem título vencido há mais de{' '}
              {preview.bloqueioFinanceiro.diasDeTolerancia} dias. Regularize até{' '}
              {dataBR(preview.bloqueioFinanceiro.regularizarAte)} para conseguir
              confirmar este pedido.
            </Text>
          </View>
        ) : preview.conjuntoInviavel ? (
          <View style={styles.blocoInviavel}>
            <Text style={styles.blocoInviavelTitulo}>
              Este pedido não pode ser confirmado
            </Text>
            <Text style={styles.blocoInviavelTexto}>
              Nenhum item está disponível em estoque no momento. Fale com seu
              vendedor para montar um novo pedido.
            </Text>
          </View>
        ) : !preview.actionable ? (
          // RN-47 — fora da janela é CONSULTA. O servidor recusa o aceite de
          // qualquer forma; aqui o cliente vê a data em vez de um botão que
          // levaria erro.
          <View style={styles.blocoConsulta}>
            <Text style={styles.blocoConsultaTexto}>
              Confirmação disponível a partir de{' '}
              {dataBR(preview.actionableFrom)}.
            </Text>
          </View>
        ) : pendentes.length ? (
          <>
            {/* Em boleto o cliente paga n vezes — comportamento normal do meio.
                Avisar antes é o que impede que o segundo boleto pareça cobrança
                repetida. */}
            {pendentes.length > 1 ? (
              <Text style={styles.avisoPagamentos}>
                São {pendentes.length} pedidos. Você paga um de cada vez e pode
                voltar pelo menu para pagar os próximos.
              </Text>
            ) : null}
            <TouchableOpacity
              style={styles.botaoPrimario}
              activeOpacity={0.8}
              disabled={confirmando}
              onPress={confirmar}>
              <Text style={styles.botaoPrimarioTexto}>
                {confirmando
                  ? 'Confirmando...'
                  : pendentes.length > 1
                  ? `Aceitar tudo e pagar o pedido ${
                      totalDeVendas - pendentes.length + 1
                    }`
                  : 'Confirmar e ir para o pagamento'}
              </Text>
            </TouchableOpacity>
          </>
        ) : (
          <View style={styles.blocoConsulta}>
            <Text style={styles.blocoConsultaTexto}>
              Todos os pedidos deste dia já foram enviados.
            </Text>
          </View>
        )}

        <TouchableOpacity
          style={styles.botaoSecundario}
          activeOpacity={0.8}
          onPress={goBack}>
          <Text style={styles.botaoSecundarioTexto}>Agora não</Text>
        </TouchableOpacity>
      </ScrollView>
    </View>
  );
};

export default ScheduledOrderReview;
